import React, { useLayoutEffect, useRef, useState } from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnType } from 'antd/es/table';
import './instrumentTables.css';

/** Fit the table's sticky header and rows above pagination. */
export default function CatalogueTable<T extends object>(props: TableProps<T>) {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollHeight, setScrollHeight] = useState(1);
  const [sort, setSort] = useState(() => {
    const initial = props.columns?.find(
      (column): column is ColumnType<T> =>
        'defaultSortOrder' in column && Boolean(column.defaultSortOrder),
    );
    return { key: initial?.key, order: initial?.defaultSortOrder ?? null };
  });
  // Keep the active choice while using current labels after catalogue edits.
  // AntD otherwise retains the original column's comparator in its sort state.
  const columns = props.columns?.map(column =>
    'sorter' in column && column.sorter
      ? { ...column, sortOrder: sort.key === column.key ? sort.order : null }
      : column,
  );

  useLayoutEffect(() => {
    const frame = ref.current;
    const table = frame?.querySelector<HTMLElement>('.ant-table-wrapper');
    if (!frame || !table) return undefined;
    const measure = () => {
      const content = table.querySelector<HTMLElement>('.ant-table-content');
      if (!content) return;
      const chrome =
        table.getBoundingClientRect().height -
        content.getBoundingClientRect().height;
      setScrollHeight(Math.max(1, Math.floor(frame.clientHeight - chrome)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    // Pagination appearing/disappearing or wrapping changes the space available.
    observer.observe(table);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="ac-table-viewport"
      ref={ref}
      style={{ '--ac-table-height': `${scrollHeight}px` } as React.CSSProperties}
    >
      <Table<T>
        {...props}
        columns={columns}
        onChange={(pagination, filters, sorter, extra) => {
          const active = Array.isArray(sorter) ? sorter[0] : sorter;
          setSort({ key: active?.columnKey, order: active?.order ?? null });
          props.onChange?.(pagination, filters, sorter, extra);
        }}
      />
    </div>
  );
}
